import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ServiceCard from './ServiceCard';
import ContactForm from './ContactForm';
import { HOMEPAGE_CAROUSEL_IMAGES, CM_MAHAL_CAROUSEL_IMAGES } from '../utils/projectImages';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const [gifLoaded, setGifLoaded] = useState(false);

  // Check if GIF exists and load it
  useEffect(() => {
    const img = new Image();
    img.src = '/construction-timelapse.gif';
    img.onload = () => {
      setGifLoaded(true);
    };
    img.onerror = () => {
      setGifLoaded(false);
    };
  }, []);

  const dhanalakshmiProjectImages = HOMEPAGE_CAROUSEL_IMAGES;
  const cmMahalProjectImages = CM_MAHAL_CAROUSEL_IMAGES;

  const services = [
    {
      id: 'dhanalakshmi-engineers',
      name: 'Dhanalakshmi Engineers',
      service: 'BUILDERS',
      description: 'With over 25 years of excellence in construction, we deliver premium residential and commercial projects. Our commitment to quality materials, skilled craftsmanship, and on-time delivery has made us a trusted name in the industry. We transform your vision into reality with precision and reliability.',
      badge: 'Since 1995'
    },
    {
      id: 'dev-design-studio',
      name: 'Dev Design Studio',
      service: 'ARCHITECTURE & PLANNING',
      description: 'Innovative architectural solutions that blend functionality with aesthetic excellence. Our expert team creates spaces that are both beautiful and practical, ensuring your project meets the highest standards of design and efficiency.',
      badge: 'Expert Design'
    },
    {
      id: 'cm-homes',
      name: 'CM Homes',
      service: 'REAL ESTATE PROMOTERS',
      description: 'Trusted real estate development with a proven track record of delivering quality properties. We specialize in residential and commercial projects that offer exceptional value, transparent dealings, and long-term investment potential.',
      badge: 'Trusted Partner'
    },
    {
      id: 'cm-mahal',
      name: 'CM Mahal',
      service: 'MARRIAGE & PARTY HALL',
      description: 'Elegant venues for your most important celebrations. Our world-class facilities, professional event management, and impeccable service ensure your special occasions are memorable and flawless.',
      badge: 'Premium Venues'
    },
    {
      id: 'cm-luxury-stays',
      name: 'CM Luxury Stays',
      service: 'LUXURY HOSPITALITY',
      description: 'Premium accommodation experiences with world-class amenities and exceptional service. We offer luxurious stays that combine comfort, elegance, and unmatched hospitality for discerning travelers.',
      badge: 'Luxury Experience'
    }
  ];

  const stats = [
    { number: '25+', label: 'Years of Excellence', icon: '🏆' },
    { number: '500+', label: 'Projects Completed', icon: '🏗️' },
    { number: '100%', label: 'Client Satisfaction', icon: '⭐' }
  ];

  const handleServiceClick = (id) => {
    navigate(`/company/${id}`);
  };

  const dhanalakshmiService = services.find((service) => service.id === 'dhanalakshmi-engineers');
  const cmMahalService = services.find((service) => service.id === 'cm-mahal');
  const otherServices = services.filter(
    (service) => service.id !== 'dhanalakshmi-engineers' && service.id !== 'cm-mahal'
  );

  return (
    <div className="homepage">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{
          ...(gifLoaded && {
            backgroundImage: 'url(/construction-timelapse.gif)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          })
        }}
      >
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="hero-title-line">Building Trust.</span>
            <span className="hero-title-line orange">Delivering Excellence.</span>
          </h1>
          <p className="hero-subtitle">
            Premium construction with 25+ years of excellence
          </p>
          <div className="hero-buttons">
            <button 
              className="hero-btn hero-btn-primary"
              onClick={() => navigate('/company/dhanalakshmi-engineers')}
            >
              View Projects
            </button>
            <button 
              className="hero-btn hero-btn-secondary"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get a Quote
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <span className="stat-icon">{stat.icon}</span>
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="services-container">
          <div className="services-header">
            <h2 className="services-title">
              Our <span className="highlight">Premium Services</span>
            </h2>
            <p className="services-description">
              CM Groups offers comprehensive solutions across construction, architecture, real estate, and event management. 
              With decades of experience, we deliver excellence in every project.
            </p>
          </div>
          
          <div className="service-showcase">
            <ServiceCard
              service={dhanalakshmiService}
              images={dhanalakshmiProjectImages}
              onClick={() => handleServiceClick(dhanalakshmiService.id)}
            />
          </div>

          <div className="service-showcase">
            <ServiceCard
              service={cmMahalService}
              images={cmMahalProjectImages}
              onClick={() => handleServiceClick(cmMahalService.id)}
            />
          </div>

          <div className="services-grid">
            {otherServices.map((service) => (
              <ServiceCard 
                key={service.id} 
                service={service} 
                onClick={() => handleServiceClick(service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="why-choose-container">
          <div className="why-choose-header">
            <h2 className="why-choose-title">
              Why Choose <span className="highlight">CM Groups</span>
            </h2>
            <p className="why-choose-description">
              Our commitment to excellence sets us apart in the construction industry
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="feature-title">Trust & Reliability</h3>
              <p className="feature-description">
                Over 25 years of proven track record. We've built lasting relationships with clients through 
                transparent communication, honest dealings, and consistent delivery on our promises.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="feature-title">Premium Quality Materials</h3>
              <p className="feature-description">
                We never compromise on quality. Every project uses only the finest materials, sourced from 
                trusted suppliers, ensuring durability and long-term value for your investment.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="feature-title">On-Time Delivery</h3>
              <p className="feature-description">
                Time is money, and we respect yours. Our efficient project management ensures timely completion 
                without compromising quality. Your deadlines are our commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2>
              Ready to Start Your <span className="highlight">Project?</span>
            </h2>
            <p>
              Let's discuss how we can bring your vision to life. Our team is ready to provide expert 
              consultation and a detailed quote for your project.
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="contact-item-content">
                  <h3>Our Location</h3>
                  <p>CM Mahal, Jothi Nagar, Madambakkam<br />Chennai 603202, India</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="contact-item-content">
                  <h3>Call Us</h3>
                  <a href="tel:+919176253687">+91 9176253687</a>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-item-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="contact-item-content">
                  <h3>Email Us</h3>
                  <a href="mailto:info@cmgroups.in">info@cmgroups.in</a>
                </div>
              </div>
            </div>
          </div>

          <ContactForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
